import {
  IconButton,
  IconButtonColorEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  NotificationTypeEnum,
  PremiumLineIcon,
  Tooltip,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useWorkspaces } from '@/features/app/api/get-workspaces';
import { useNotificationStore } from '@/stores/notifications';
import { useSelectedItemsStore } from '@/stores/selected-items';
import { downloadBlob } from '@/utils/files';
import { formatInAppTz } from '@/utils/spain-time';

import {
  getSponsorshipReportClipIds,
  SPONSORSHIP_REPORT_MAX_CLIPS,
  usePostSponsorshipReport,
} from '../../api/post-sponsorship-report';
import { useClipListFilters } from '../../hooks/use-clip-list-filters';
import { useClipPermissions } from '../../hooks/useClipPermissions';

/**
 * Fixed so the "in progress" notification can be taken down once the report is
 * done. Negative, so it can never match the ids the store hands out (`++index`).
 */
export const SPONSORSHIP_REPORT_PROGRESS_NOTIFICATION_ID = -6282;

/**
 * The workspace only pre-fills the report's Link column, so it is sent when the
 * filter leaves no doubt about which one it is, and left out otherwise rather
 * than blocking the export.
 */
function getSponsorshipReportWorkspaceId(workspaces: string[]): string | null {
  return workspaces.length === 1 ? (workspaces[0] ?? null) : null;
}

/** Lowercase ASCII words joined by `_`, safe in a file name on any system. */
function toFilenameSegment(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Named after the workspace the report is for, when there is one, and the
 * Madrid time it was generated at (`yyyyMMddHHmmss`), like every other date the
 * report carries. Down to the second, so exporting again does not produce a
 * file named like the previous one.
 */
export function getSponsorshipReportFilename(
  workspaceName?: string | null,
  now: Date = new Date()
): string {
  const workspace = workspaceName ? toFilenameSegment(workspaceName) : '';
  const timestamp = formatInAppTz(now, 'yyyyMMddHHmmss');

  return workspace ? `patrocinio_${workspace}_${timestamp}.xlsx` : `patrocinio_${timestamp}.xlsx`;
}

export function ClipSponsorshipReportButton() {
  const { t } = useTranslation();
  const { canExportSponsorshipReport } = useClipPermissions();
  const { addErrorNotification, addWarningNotification, addNotificationId, dismissNotification } =
    useNotificationStore();
  const selectedIds = useSelectedItemsStore(state => state.include);
  const isAllSelected = useSelectedItemsStore(state => state.toggleAllActived);
  // The filters the list is requested with, debounced as it debounces them: the
  // workspace sent and named in the file, and the clips "select all" stands for,
  // are those of the list on screen.
  const listFilters = useClipListFilters();
  const workspaces = listFilters.workspaces ?? [];
  // Already cached by the workspace filter, so this costs no extra request.
  const { data: workspaceList = [] } = useWorkspaces();
  const { mutate: generateReport, isPending } = usePostSponsorshipReport();
  const [isResolvingClips, setIsResolvingClips] = useState(false);

  if (!canExportSponsorshipReport) return null;

  const workspaceId = getSponsorshipReportWorkspaceId(workspaces);
  const workspaceName = workspaceList.find(({ id }) => id === workspaceId)?.name;

  const isExceedingLimit = selectedIds.length > SPONSORSHIP_REPORT_MAX_CLIPS;
  const isDisabled = selectedIds.length === 0 || isExceedingLimit || isPending || isResolvingClips;

  /**
   * Under "select all" the report covers every clip the filters match, not only
   * the pages loaded so far, so their ids are asked for here, and only here: it
   * costs one request per export rather than one per "select all".
   */
  const resolveClipIds = async (): Promise<string[] | null> => {
    if (!isAllSelected) return selectedIds;

    setIsResolvingClips(true);
    try {
      const clipIds = await getSponsorshipReportClipIds(listFilters);

      if (clipIds.length > SPONSORSHIP_REPORT_MAX_CLIPS) {
        addWarningNotification(
          t('El filtro tiene más de {{max}} clips. Acótalo para exportar el patrocinio', {
            max: SPONSORSHIP_REPORT_MAX_CLIPS,
          })
        );
        return null;
      }
      // The list emptied between loading and exporting: nothing to report on.
      return clipIds.length > 0 ? clipIds : null;
    } catch (error) {
      addErrorNotification(t('No se ha podido generar el excel de patrocinio'));
      console.error(error);
      return null;
    } finally {
      setIsResolvingClips(false);
    }
  };

  // The selection is left as it is: exporting changes nothing, so the same
  // clips can be exported again.
  const handleClick = async () => {
    // The report is built inside the request and can take several seconds, so
    // say so until it is ready (or fails); the outcome has its own notification.
    addNotificationId({
      id: SPONSORSHIP_REPORT_PROGRESS_NOTIFICATION_ID,
      type: NotificationTypeEnum.INFO,
      title: t('Generando el excel de patrocinio'),
      message: t('Puede tardar unos segundos'),
      infinite: true,
    });
    const dismissProgress = () => dismissNotification(SPONSORSHIP_REPORT_PROGRESS_NOTIFICATION_ID);

    const clipIds = await resolveClipIds();
    if (!clipIds) {
      dismissProgress();
      return;
    }

    generateReport(
      {
        clip_ids: clipIds,
        workspace_id: workspaceId,
      },
      {
        onSettled: dismissProgress,
        onSuccess: workbook => downloadBlob(workbook, getSponsorshipReportFilename(workspaceName)),
      }
    );
  };

  return (
    <Tooltip
      help={
        isExceedingLimit
          ? t('Selecciona como máximo {{max}} clips para exportar el patrocinio', {
              max: SPONSORSHIP_REPORT_MAX_CLIPS,
            })
          : t('Patrocinio')
      }
      grow
    >
      <IconButton
        onClick={handleClick}
        size={IconButtonSizeEnum.XS}
        color={IconButtonColorEnum.NONE_DEFAULT}
        disabled={isDisabled}
        // A disabled button fires no mouse events in many browsers, so the
        // tooltip wrapper would never see the pointer and the "at most 500
        // clips" explanation would never show. Let the pointer through to it.
        className={clsx(isDisabled && 'pointer-events-none')}
        data-testid="clip-sponsorship-report-button"
      >
        <PremiumLineIcon disabled={isDisabled} size={IconSizeEnum.SM} />
      </IconButton>
    </Tooltip>
  );
}
